/**
 * LabelFlow : ajout de bouteilles par l'étiquette.
 * Parcours fluidifié : on capture d'abord TOUTES les bouteilles (étiquette + contre-étiquette
 * facultative / auto), puis on traite chacune (Identification → Vérification → Enregistré).
 * L'IA pré-remplit, l'humain valide. Chaque photo peut être reprise sans tout recommencer.
 */
import { useState, useEffect } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Check, RotateCcw, Camera, X, ChevronRight, ImageUp } from 'lucide-react';
import { scannerApi, caveApi, metaApi } from '../../services/api';

/** Pivote une image base64 (sens horaire, multiples de 90°) via un canvas. Renvoie le base64 nu. */
async function rotateBase64(b64, deg) {
  const d = ((Number(deg) || 0) % 360 + 360) % 360;
  if (!d) return b64;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const swap = d === 90 || d === 270;
        canvas.width = swap ? img.height : img.width;
        canvas.height = swap ? img.width : img.height;
        const ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((d * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        resolve(canvas.toDataURL('image/jpeg', 0.9).split(',')[1]);
      } catch { resolve(b64); }
    };
    img.onerror = () => resolve(b64);
    img.src = `data:image/jpeg;base64,${b64}`;
  });
}
import { useUndoToast } from '../../hooks/useUndoToast';
import { Button } from '../../components/ui/kit';
import { StepIndicator } from '../../components/ui/StepIndicator';
import { QuantityStepper } from '../../components/ui/QuantityStepper';
import { WineColorDot, SourceBadge } from '../../components/wine/wine';
import { AppellationField, AppellationHint } from '../../components/wine/AppellationField';
import { invalidateStock } from '../../services/refresh';
import { fileToBase64, fileToPreview } from '../../utils/image';
import { TASTING_CONTEXTS } from '../../constants';
import CameraView from './CameraView';

const STEPS = ['Identification', 'Vérification', 'Enregistré'];

/** Convertit une image dataURL (aperçu) en fichier téléversable. */
function dataUrlToFile(dataUrl, name) {
  const [head, body] = dataUrl.split(',');
  const mime = head.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type: mime });
}

/** Champ de formulaire avec source et indicateur « À vérifier » si confiance faible. */
function Field({ label, value, onChange, source, confidence, testId, type = 'text' }) {
  const toVerify = confidence !== undefined && confidence < 0.6;
  return (
    <label className="block mb-3">
      <span className="flex items-center justify-between mb-1">
        <span className="text-[12px] text-n600">{label}</span>
        {source && <SourceBadge source={source} toVerify={toVerify} />}
      </span>
      <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`vd-input h-9 ${toVerify ? 'border-gold ring-1 ring-gold' : ''}`} />
      {toVerify && <span className="text-[11px] text-gold700">À vérifier</span>}
    </label>
  );
}

/** Vignette d'une photo capturée, avec bouton « reprendre ». */
function Thumb({ src, label, onRetake, testId }) {
  return (
    <div className="relative">
      <div className="vd-plate w-24 h-32 bg-surface flex items-center justify-center overflow-hidden">
        <img src={src} alt="" className="max-w-full max-h-full object-contain rounded" />
      </div>
      <span className="absolute bottom-1 left-1 text-[10px] bg-paper/90 px-1.5 rounded">{label}</span>
      {onRetake && (
        <button onClick={onRetake} data-testid={testId}
          className="absolute top-1 right-1 bg-paper/90 rounded-full p-1 text-n700 hover:text-gold700" title="Reprendre">
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default function LabelFlow({ initialImages, initialPreviews, enrich, tasteOnly, onCancel, onScanNext }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { notify } = useUndoToast();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: metaApi.settings, staleTime: 60000 });
  const autostart = !!settings?.back_label_autostart;

  // ----- Phase de capture (multi-bouteilles) -----
  const [phase, setPhase] = useState('capture'); // 'capture' | 'process' | 'done'
  const [bottles, setBottles] = useState([]);     // bouteilles complètes { images, previews }
  const [draft, setDraft] = useState({ images: initialImages, previews: initialPreviews });
  const [captureKind, setCaptureKind] = useState(null); // 'front' | 'back' | 'front-retake' | 'back-retake'
  const [autoInit, setAutoInit] = useState(false);
  const [importing, setImporting] = useState(false);

  // Démarrage auto de la contre-étiquette pour la 1re bouteille (réglage Paramètres).
  useEffect(() => {
    if (settings && !autoInit) {
      setAutoInit(true);
      if (autostart && draft && draft.images.length === 1) setCaptureKind('back');
    }
  }, [settings, autoInit, autostart, draft]);

  const onCapture = (b64, prev) => {
    if (captureKind === 'front') {
      setDraft({ images: [b64], previews: [prev] });
      setCaptureKind(autostart ? 'back' : null);
    } else if (captureKind === 'front-retake') {
      setDraft((d) => ({ images: [b64, ...d.images.slice(1)], previews: [prev, ...d.previews.slice(1)] }));
      setCaptureKind(null);
    } else if (captureKind === 'back') {
      setDraft((d) => ({ images: [...d.images, b64], previews: [...d.previews, prev] }));
      setCaptureKind(null);
    } else if (captureKind === 'back-retake') {
      setDraft((d) => ({ images: [d.images[0], b64], previews: [d.previews[0], prev] }));
      setCaptureKind(null);
    }
  };

  // Import de 1 ou 2 photos à la fois ; pour 2, l'IA distingue étiquette (recto) / contre-étiquette (verso).
  const importFiles = async (fileList) => {
    const list = Array.from(fileList || []).slice(0, 2);
    if (!list.length) return;
    setImporting(true);
    try {
      const pics = await Promise.all(list.map(async (f) => ({ b64: await fileToBase64(f), prev: await fileToPreview(f) })));
      if (pics.length === 1) {
        let order = {};
        try { order = await scannerApi.orderLabels([pics[0].b64]); } catch { /* orientation par défaut */ }
        const b64 = await rotateBase64(pics[0].b64, order.rotate_front);
        setDraft({ images: [b64], previews: [`data:image/jpeg;base64,${b64}`] });
        setCaptureKind(null);
      } else {
        let order = { front: 0, back: 1 };
        try { order = await scannerApi.orderLabels([pics[0].b64, pics[1].b64]); } catch { /* ordre par défaut */ }
        const fr = pics[order.front] ?? pics[0];
        const bk = pics[order.back] ?? pics[1];
        const frB64 = await rotateBase64(fr.b64, order.rotate_front);
        const bkB64 = await rotateBase64(bk.b64, order.rotate_back);
        setDraft({ images: [frB64, bkB64], previews: [`data:image/jpeg;base64,${frB64}`, `data:image/jpeg;base64,${bkB64}`] });
        setCaptureKind(null);
        toast.success("Photos triées et réorientées par l'IA : étiquette et contre-étiquette");
      }
    } catch {
      toast.error('Import impossible — réessayez');
    } finally {
      setImporting(false);
    }
  };

  const nextBottle = () => {
    if (!draft) return;
    setBottles((b) => [...b, draft]);
    setDraft(null);
    setCaptureKind('front');
  };

  const retakeBottle = (idx) => {
    // Remet une bouteille déjà capturée en brouillon pour reprendre ses photos.
    setBottles((b) => {
      const target = b[idx];
      setDraft(target);
      setCaptureKind(null);
      return b.filter((_, i) => i !== idx);
    });
  };

  const removeBottle = (idx) => setBottles((b) => b.filter((_, i) => i !== idx));

  // ----- Phase de traitement (par bouteille) -----
  const [procIndex, setProcIndex] = useState(0);
  const [step, setStep] = useState('analyzing'); // 'analyzing' | 'identify' | 'verify' | 'saved'
  const [progress, setProgress] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ quantity: 12, on_wine_list: true, format: '750', context: 'amis', place: '', date: new Date().toISOString().slice(0, 10), price_public: '' });
  const [results, setResults] = useState([]);
  const [recapture, setRecapture] = useState(null); // null | 'front' | 'back' — reprise d'une photo pendant le traitement
  const [saving, setSaving] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const analyzeBottle = async (imgs) => {
    setStep('analyzing'); setAnalysis(null); setSelected(null);
    setForm({ quantity: 12, on_wine_list: true, format: '750', context: 'amis', place: '', date: new Date().toISOString().slice(0, 10), price_public: '' });
    setProgress('Lecture de l’étiquette et de la contre-étiquette…');
    const t1 = setTimeout(() => setProgress('Comparaison avec votre cave…'), 1200);
    const t2 = setTimeout(() => setProgress('Recherche web et site du domaine…'), 2400);
    try {
      const res = await scannerApi.analyzeLabel(imgs, enrich);
      setAnalysis(res);
      setStep('identify');
    } catch {
      toast.error("Échec de l'analyse — vous pouvez reprendre les photos");
      setStep('error');
    } finally {
      clearTimeout(t1); clearTimeout(t2);
    }
  };

  const startProcessing = () => {
    const all = draft ? [...bottles, draft] : bottles;
    if (!all.length) return;
    setBottles(all);
    setDraft(null);
    setPhase('process');
    setProcIndex(0);
    setResults([]);
    analyzeBottle(all[0].images);
  };

  const prefillLocation = async (fmt, qty) => {
    try { const loc = await scannerApi.suggestLocation(fmt, qty); set('location_code')(loc?.code); } catch { /* ignore */ }
  };

  const chooseCandidate = async (cand) => {
    const ex = analysis.extraction;
    const fmt = ex.format || '750';
    if (cand === 'new') {
      const en = analysis.enrichment || {};
      setSelected('new');
      setForm((f) => ({
        ...f, format: fmt, year: ex.millesime || '', name: ex.cuvee || '', domaine: ex.domaine || ex.propriete || '',
        appellation: ex.appellation || '', color: ex.couleur || 'rouge', type: ex.type || 'tranquille',
        cepages: (ex.cepages || en.cepages || []).join(', '), degre: ex.degre || '',
        presentation: en.presentation || '', region: ex.region || en.region || '',
        address: ex.adresse || en.address || '', postal_code: ex.code_postal || '', city: ex.ville || en.city || '',
        purchase: f.purchase || '', price_table: f.price_table || '',
      }));
    } else {
      setSelected(cand.cuvee_id);
      setForm((f) => ({
        ...f, format: fmt, year: ex.millesime || '', name: cand.name, domaine: cand.domain_name,
        appellation: cand.appellation || '', color: cand.color, cepages: (cand.cepages || []).join(', '),
        degre: ex.degre || '', cuvee_id: cand.cuvee_id,
      }));
    }
    await prefillLocation(fmt, form.quantity);
    setStep('verify');
  };

  const advance = (savedRes) => {
    const next = procIndex + 1;
    setResults((r) => [...r, savedRes]);
    if (next < bottles.length) {
      setProcIndex(next);
      analyzeBottle(bottles[next].images);
    } else {
      setStep('done');
      setPhase('done');
    }
    invalidateStock(qc);
  };

  // Reprise d'une photo pendant le traitement puis ré-analyse de la bouteille courante.
  const onRecapture = (b64, prev) => {
    const kind = recapture;
    const cur = bottles[procIndex];
    let images; let previews;
    if (kind === 'front') {
      images = [b64, ...cur.images.slice(1)]; previews = [prev, ...cur.previews.slice(1)];
    } else if (cur.images.length > 1) {
      images = [cur.images[0], b64]; previews = [cur.previews[0], prev];
    } else {
      images = [...cur.images, b64]; previews = [...cur.previews, prev];
    }
    setBottles((bs) => bs.map((b, i) => (i === procIndex ? { images, previews } : b)));
    setRecapture(null);
    analyzeBottle(images);
  };

  const skipCurrent = () => advance({ added: 0, cuvee_name: '(ignorée)' });

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
    const purchase = form.purchase ? parseFloat(form.purchase) : null;
    const priceTable = form.price_table ? parseFloat(form.price_table) : (purchase ? Math.round(purchase * 3) : null);
    const payload = {
      year: form.year || 'NV', format: form.format, quantity: form.quantity,
      purchase_price_ht: purchase, price_table: priceTable, location_code: form.location_code,
      on_wine_list: form.on_wine_list, taste_only: !!tasteOnly,
    };
    if (tasteOnly) {
      payload.tasting_context = form.context || 'amis';
      payload.place = form.place || '';
      payload.tasting_date = form.date || null;
      payload.price_seen = (form.price_public === '' || form.price_public == null) ? null : parseFloat(form.price_public);
    }
    if (selected === 'new') {
      payload.new_cuvee = {
        name: form.name, color: form.color, type: form.type, appellation: form.appellation,
        region: form.region, degre: form.degre,
        cepages: (form.cepages || '').split(',').map((s) => ({ nom: s.trim() })).filter((c) => c.nom),
        presentation: form.presentation,
      };
      payload.new_domain = {
        name: form.domaine, region: form.region, appellations: form.appellation ? [form.appellation] : [],
        address: form.address || null, postal_code: form.postal_code || null, city: form.city || null,
        site_web: form.site_web || (analysis?.enrichment || {}).site_web || null,
        logo_url: (analysis?.enrichment || {}).logo_url || null,
      };
    } else {
      payload.cuvee_id = form.cuvee_id;
    }
    const res = await scannerApi.entry(payload);
    // Conserve les photos étiquette / contre-étiquette sur la fiche (nouvelle ou existante).
    const previews = bottles[procIndex]?.previews || [];
    if (res.cuvee_id && previews.length) {
      const kinds = ['label', 'back_label'];
      await Promise.allSettled(previews.map((prev, i) =>
        caveApi.uploadImage(res.cuvee_id, kinds[i] || 'label', dataUrlToFile(prev, `${kinds[i] || 'photo'}.jpg`))));
    }
    if (tasteOnly) {
      toast.success(`Vin dégusté catalogué : ${res.cuvee_name}`);
    } else {
      toast.success(`${res.added} bouteille(s) ajoutée(s) : ${res.cuvee_name}`);
      notify(`${res.added} bouteille(s) ajoutée(s)`, () => scannerApi.undo(res.group_id).then(() => {
        invalidateStock(qc);
      }));
    }
    advance(res);
    } catch (e) {
      toast.error(e.message || "Échec de l'enregistrement — réessayez");
    } finally {
      setSaving(false);
    }
  };

  const ex = analysis?.extraction || {};
  const conf = ex.confiance_par_champ || {};
  const stepIndex = { analyzing: 0, error: 0, identify: 0, verify: 1, saved: 2, done: 2 }[step];

  /* ---------------- PHASE CAPTURE ---------------- */
  if (phase === 'capture') {
    const showFrontCam = !draft || captureKind === 'front';
    const showBackCam = captureKind === 'back' || captureKind === 'back-retake';
    const showFrontRetakeCam = captureKind === 'front-retake';
    const camActive = showFrontCam || showBackCam || showFrontRetakeCam;

    return (
      <div className="max-w-4xl" data-testid="labelflow-capture">
        {/* Bouteilles déjà capturées */}
        {bottles.length > 0 && (
          <div className="mb-5">
            <p className="kicker mb-2">{bottles.length} bouteille(s) capturée(s)</p>
            <div className="flex flex-wrap gap-3">
              {bottles.map((b, i) => (
                <div key={i} className="relative vd-card p-2" data-testid={`captured-bottle-${i}`}>
                  <div className="flex gap-1">
                    {b.previews.map((p, j) => (
                      <div key={j} className="vd-plate w-16 h-20 bg-surface flex items-center justify-center overflow-hidden">
                        <img src={p} alt="" className="max-w-full max-h-full object-contain rounded" />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[11px] text-n600">N°{i + 1}</span>
                    <span className="flex gap-1">
                      <button onClick={() => retakeBottle(i)} className="text-n600 hover:text-gold700" title="Reprendre" data-testid={`retake-bottle-${i}`}><RotateCcw className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeBottle(i)} className="text-n600 hover:text-red-700" title="Retirer" data-testid={`remove-bottle-${i}`}><X className="w-3.5 h-3.5" /></button>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="font-title text-2xl mb-1" data-testid="capture-heading">
          {draft ? `Bouteille n°${bottles.length + 1}` : `Prochaine bouteille n°${bottles.length + 1}`}
        </p>
        <p className="text-[13px] text-n600 mb-4">
          {showBackCam ? 'Photographiez la contre-étiquette.' : showFrontCam || showFrontRetakeCam ? 'Photographiez l’étiquette.' : 'Étiquette capturée. Ajoutez la contre-étiquette ou passez à la suite.'}
        </p>

        {/* Brouillon en cours : vignettes + reprise */}
        {draft && !camActive && (
          <div className="flex flex-wrap gap-3 mb-4">
            <Thumb src={draft.previews[0]} label="Étiquette" onRetake={() => setCaptureKind('front-retake')} testId="retake-front" />
            {draft.previews[1]
              ? <Thumb src={draft.previews[1]} label="Contre-étiq." onRetake={() => setCaptureKind('back-retake')} testId="retake-back" />
              : <button onClick={() => setCaptureKind('back')} data-testid="add-back-label" className="vd-plate w-24 h-32 border-dashed flex flex-col items-center justify-center text-[12px] text-n600 gap-1"><Camera className="w-5 h-5" /> + Contre-étiquette</button>}
          </div>
        )}

        {/* Caméra */}
        {camActive && (
          <div className="mb-4 max-w-md">
            <CameraView key={`${captureKind}-${bottles.length}`} compact onResult={onCapture} captureLabel="Capturer" />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {draft && !camActive && (
            <Button onClick={nextBottle} data-testid="next-bottle-btn"><Camera className="w-4 h-4" /> Bouteille suivante</Button>
          )}
          <label className="vd-btn vd-btn-secondary cursor-pointer" data-testid="import-pair-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageUp className="w-4 h-4" />}
            Importer 1 ou 2 photos
            <input type="file" accept="image/*" multiple className="hidden" disabled={importing}
              onChange={(e) => { importFiles(e.target.files); e.target.value = ''; }} />
          </label>
          <Button variant="primary" onClick={startProcessing} disabled={!draft && !bottles.length} data-testid="no-more-bottles-btn">
            Je n'ai pas d'autre bouteille — analyser ({(draft ? 1 : 0) + bottles.length})
          </Button>
        </div>
        <p className="text-[12px] text-n500 mt-2">Astuce : importez l'étiquette et la contre-étiquette ensemble, l'IA les reconnaît automatiquement.</p>
        <button onClick={onCancel} className="vd-link text-[13px] mt-6 block" data-testid="cancel-flow">Annuler</button>
      </div>
    );
  }

  /* ---------------- PHASE TRAITEMENT ---------------- */
  return (
    <div className="max-w-4xl" data-testid="labelflow-process">
      <div className="flex items-center justify-between mb-3">
        <p className="kicker" data-testid="proc-progress">Bouteille {Math.min(procIndex + 1, bottles.length)} / {bottles.length}</p>
      </div>
      {step !== 'done' && <div className="mb-5"><StepIndicator steps={STEPS} current={stepIndex} /></div>}

      {/* Photos prises pour la bouteille en cours — reprise possible (même après un échec). */}
      {['identify', 'verify', 'error'].includes(step) && (
        recapture ? (
          <div className="mb-4 max-w-md" data-testid="proc-recapture">
            <p className="text-[13px] text-n600 mb-2">Reprenez la {recapture === 'front' ? 'photo de l’étiquette' : 'photo de la contre-étiquette'}.</p>
            <CameraView key={`recap-${recapture}-${procIndex}`} compact onResult={onRecapture} captureLabel="Utiliser cette photo" />
            <button onClick={() => setRecapture(null)} className="vd-link text-[13px] mt-2" data-testid="cancel-recapture">Annuler la reprise</button>
          </div>
        ) : (
          <div className="flex flex-wrap items-start gap-3 mb-4" data-testid="proc-photos">
            <p className="w-full kicker">Photos prises</p>
            <Thumb src={bottles[procIndex]?.previews?.[0]} label="Étiquette" onRetake={() => setRecapture('front')} testId="proc-retake-front" />
            {bottles[procIndex]?.previews?.[1]
              ? <Thumb src={bottles[procIndex].previews[1]} label="Contre-étiq." onRetake={() => setRecapture('back')} testId="proc-retake-back" />
              : <button onClick={() => setRecapture('back')} data-testid="proc-add-back" className="vd-plate w-24 h-32 border-dashed flex flex-col items-center justify-center text-[12px] text-n600 gap-1"><Camera className="w-5 h-5" /> + Contre-étiquette</button>}
          </div>
        )
      )}

      {(step === 'analyzing') && (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="analyzing">
          <Loader2 className="w-8 h-8 animate-spin text-gold700 mb-4" />
          <p className="font-title text-2xl">Analyse en cours…</p>
          <p className="text-n600 text-[13px] mt-2">{progress}</p>
        </div>
      )}

      {step === 'error' && !recapture && (
        <div className="text-center py-16" data-testid="analyze-error">
          <p className="font-title text-2xl mb-2">Analyse impossible</p>
          <p className="text-n600 text-[13px] mb-4">La photo de la bouteille {procIndex + 1} n'a pas pu être lue. Reprenez les photos ci-dessus, réessayez ou ignorez.</p>
          <div className="flex justify-center gap-2">
            <Button variant="primary" onClick={() => analyzeBottle(bottles[procIndex].images)} data-testid="retry-analyze"><RotateCcw className="w-4 h-4" /> Réessayer</Button>
            <Button onClick={() => setRecapture('front')} data-testid="error-retake"><Camera className="w-4 h-4" /> Reprendre les photos</Button>
            <Button onClick={() => advance({ added: 0, cuvee_name: '(ignorée)' })} data-testid="skip-bottle">Ignorer cette bouteille</Button>
          </div>
        </div>
      )}

      {step === 'identify' && analysis && !recapture && (
        <div data-testid="identify-step">
          <p className="kicker mb-3">Correspondances</p>
          <div className="space-y-2">
            {analysis.candidates.map((c) => (
              <button key={c.cuvee_id} onClick={() => chooseCandidate(c)} data-testid={`candidate-${c.cuvee_id}`}
                className="w-full flex items-center gap-3 p-3 vd-card hover:bg-surface text-left">
                <span className="font-title text-2xl text-gold700 nums w-14">{c.score}%</span>
                <span className="flex-1">
                  <span className="flex items-center gap-2"><WineColorDot color={c.color} /><span className="font-title text-lg">{c.name}</span></span>
                  <span className="text-[12px] text-n600">{c.domain_name} · {c.appellation}</span>
                </span>
                <span className="vd-tag vd-tag-fill">{c.status}</span>
              </button>
            ))}
            <button onClick={() => chooseCandidate('new')} data-testid="candidate-new"
              className="w-full p-3 vd-card hover:bg-surface text-left text-gold700">
              Aucune — créer une nouvelle cuvée
            </button>
          </div>
        </div>
      )}

      {step === 'verify' && !recapture && (
        <div className="grid md:grid-cols-[1fr_300px] gap-6" data-testid="verify-step">
          <div>
            <p className="kicker mb-3">Fiche pré-remplie</p>
            <div className="grid grid-cols-2 gap-x-4">
              <Field label="Cuvée" value={form.name} onChange={set('name')} source="etiquette" confidence={conf.cuvee} testId="verify-name" />
              <Field label="Millésime" value={form.year} onChange={set('year')} source="etiquette" confidence={conf.millesime} testId="verify-year" />
              <Field label="Domaine" value={form.domaine} onChange={set('domaine')} source="etiquette" confidence={conf.domaine} testId="verify-domaine" />
              <label className="block mb-3">
                <span className="flex items-center justify-between mb-1">
                  <span className="text-[12px] text-n600">Appellation</span>
                  <SourceBadge source="etiquette" toVerify={conf.appellation !== undefined && conf.appellation < 0.6} />
                </span>
                <AppellationField value={form.appellation} onSave={(v) => set('appellation')(v)}
                  onPick={({ vignoble, cepages }) => {
                    if (vignoble && !form.region) set('region')(vignoble);
                    const cs = (cepages || []).filter(Boolean);
                    if (cs.length && !(form.cepages || '').trim()) set('cepages')(cs.join(', '));
                  }}
                  testId="verify-appellation" inputClassName="h-9 w-full" displayClassName="vd-input h-9 flex items-center w-full" />
                <AppellationHint value={form.appellation} />
              </label>
              <Field label="Cépages" value={form.cepages} onChange={set('cepages')} source={selected === 'new' ? 'web' : 'etiquette'} testId="verify-cepages" />
              <Field label="Degré" value={form.degre} onChange={set('degre')} source="etiquette" testId="verify-degre" />
            </div>
            {selected === 'new' && (
              <>
                <div className="grid grid-cols-2 gap-x-4">
                  <Field label="Région" value={form.region} onChange={set('region')} source="etiquette" testId="verify-region" />
                  <Field label="Adresse (embouteilleur)" value={form.address} onChange={set('address')} source="etiquette" testId="verify-address" />
                  <Field label="Code postal" value={form.postal_code} onChange={set('postal_code')} source="etiquette" testId="verify-postal" />
                  <Field label="Ville" value={form.city} onChange={set('city')} source="etiquette" testId="verify-city" />
                </div>
                <label className="block mb-3">
                  <span className="flex items-center justify-between mb-1"><span className="text-[12px] text-n600">Présentation œnologique</span><SourceBadge source="domaine" /></span>
                  <textarea value={form.presentation || ''} onChange={(e) => set('presentation')(e.target.value)} rows={3} className="vd-input h-auto py-2" data-testid="verify-presentation" />
                </label>
              </>
            )}
          </div>
          <div className="md:border-l md:border-hair md:pl-5">
            {tasteOnly ? (
              <>
                <p className="kicker mb-2">Dégustation</p>
                <label className="block mb-3">
                  <span className="text-[12px] text-n600 block mb-1">Contexte</span>
                  <select value={form.context || 'amis'} onChange={(e) => set('context')(e.target.value)} className="vd-input h-9" data-testid="verify-context">
                    {TASTING_CONTEXTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </label>
                <label className="block mb-3">
                  <span className="text-[12px] text-n600 block mb-1">Lieu</span>
                  <input value={form.place || ''} onChange={(e) => set('place')(e.target.value)} className="vd-input h-9" data-testid="verify-place" placeholder="Salon, restaurant, domaine…" />
                </label>
                <label className="block mb-3">
                  <span className="text-[12px] text-n600 block mb-1">Date</span>
                  <input type="date" value={form.date || ''} onChange={(e) => set('date')(e.target.value)} className="vd-input h-9" data-testid="verify-date" />
                </label>
                <label className="block mb-3">
                  <span className="text-[12px] text-n600 block mb-1">Prix d'achat HT (€)</span>
                  <input value={form.purchase || ''} onChange={(e) => set('purchase')(e.target.value)} className="vd-input h-9" data-testid="verify-purchase" />
                </label>
                <label className="block mb-4">
                  <span className="text-[12px] text-n600 block mb-1">Prix TTC public constaté (€)</span>
                  <input value={form.price_public || ''} onChange={(e) => set('price_public')(e.target.value)} className="vd-input h-9" data-testid="verify-price-public" />
                </label>
              </>
            ) : (
              <>
                <p className="kicker mb-2">Quantité reçue</p>
                <QuantityStepper value={form.quantity} onChange={(v) => { set('quantity')(v); prefillLocation(form.format, v); }} testId="verify-qty" />
                <label className="block mt-4 mb-3">
                  <span className="text-[12px] text-n600 block mb-1">Prix d'achat HT (€)</span>
                  <input value={form.purchase || ''} onChange={(e) => set('purchase')(e.target.value)} className="vd-input h-9" data-testid="verify-purchase" />
                </label>
                <label className="block mb-3">
                  <span className="text-[12px] text-n600 block mb-1">Emplacement proposé</span>
                  <input value={form.location_code || ''} onChange={(e) => set('location_code')(e.target.value)} className="vd-input h-9" data-testid="verify-location" />
                </label>
                <label className="block mb-3">
                  <span className="text-[12px] text-n600 block mb-1">Prix de table suggéré (€)</span>
                  <input value={form.price_table ?? (form.purchase ? Math.round(parseFloat(form.purchase) * 3) : '')} onChange={(e) => set('price_table')(e.target.value)} className="vd-input h-9" data-testid="verify-price-table" />
                </label>
                <label className="flex items-center gap-2 text-[14px] mb-4 cursor-pointer">
                  <input type="checkbox" checked={form.on_wine_list} onChange={(e) => set('on_wine_list')(e.target.checked)} className="accent-[#b68235] w-4 h-4" data-testid="verify-on-list" />
                  Sur la carte des vins
                </label>
              </>
            )}
            <Button variant="primary" className="w-full" onClick={save} disabled={saving} data-testid="save-entry-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {tasteOnly ? 'Cataloguer ce vin dégusté' : `Enregistrer ${form.quantity} bouteilles`} {bottles.length > 1 ? `· suivante (${procIndex + 1}/${bottles.length})` : ''}
            </Button>
            <button onClick={skipCurrent} data-testid="skip-current-btn"
              className="vd-link text-[13px] mt-3 block w-full text-center">
              Annuler cette bouteille — suivante ({procIndex + 1}/{bottles.length})
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="text-center py-12" data-testid="done-step">
          <div className="w-14 h-14 rounded-full bg-gold text-white flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7" strokeWidth={2} /></div>
          {tasteOnly ? (
            <p className="font-title text-3xl">{results.filter((r) => r.cuvee_id).length} vin(s) dégusté(s) catalogué(s)</p>
          ) : (
            <>
              <p className="font-title text-3xl">{results.reduce((s, r) => s + (r.added || 0), 0)} bouteilles ajoutées</p>
              <p className="text-n600 mt-1">{results.filter((r) => r.added).length} référence(s) traitée(s)</p>
            </>
          )}
          <p className="text-[12px] text-n500 mt-2">Appuyez sur une ligne pour ouvrir la fiche.</p>
          <div className="mt-3 max-w-md mx-auto text-left space-y-1">
            {results.filter((r) => r.cuvee_id).map((r, i) => (
              <button key={i} onClick={() => navigate(`/cave/cuvee/${r.cuvee_id}`)} data-testid={`done-bottle-${r.cuvee_id}`}
                className="w-full flex justify-between items-center text-[13px] hair-b py-1.5 hover:bg-surface rounded px-1 text-left transition-colors">
                <span className="font-title text-[15px]">{r.cuvee_name} {r.year || ''}</span>
                <span className="text-n500 flex items-center gap-1">{tasteOnly ? 'dégusté' : `${r.location_code || '—'} · ${r.added} btl`}<ChevronRight className="w-4 h-4" /></span>
              </button>
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-6">
            <Button onClick={() => navigate('/cave')} data-testid="see-cave-btn">Voir la cave</Button>
            <Button variant="primary" onClick={onScanNext} data-testid="scan-next-btn"><RotateCcw className="w-4 h-4" /> Nouveau scan</Button>
          </div>
        </div>
      )}
    </div>
  );
}
